import { RouteLocationRaw } from 'vue-router'

import { LicenseType } from '@/types/company'

interface DefaultModuleRoute {
    route: RouteLocationRaw
    licenses: LicenseType[]
}

interface ModuleInformation {
    translation: string
    readonlyLicense?: LicenseType
}

export const constrainedModuleLicenses = [
    LicenseType.ECMRModule,
    LicenseType.DamageReportModule,
    LicenseType.DriverReportModule,
    LicenseType.PackagingModule,
    LicenseType.DeliveryManagementModule,
    LicenseType.PODModule,
    LicenseType.DashboardModule,
    LicenseType.CargoLoadingModule,
] as const

// We'll need to create a mutable array or else TypeScript will not accept LicenseType[] as a parameter
export const moduleLicenses = [...constrainedModuleLicenses]

export type ModuleLicenseType = (typeof constrainedModuleLicenses)[number]

export const readonlyLicenses = [
    LicenseType.PackagingModuleReadonly,
    LicenseType.PODModuleReadonly,
    LicenseType.DeliveryManagementModuleReadonly,
    LicenseType.DamageReportModuleReadonly,
    LicenseType.DriverReportModuleReadonly,
]

export const moduleInformation: Record<ModuleLicenseType, ModuleInformation> = {
    [LicenseType.ECMRModule]: {
        translation: 'ecmr',
    },
    [LicenseType.DamageReportModule]: {
        translation: 'damageReports',
        readonlyLicense: LicenseType.DamageReportModuleReadonly,
    },
    [LicenseType.DriverReportModule]: {
        translation: 'driverReports',
        readonlyLicense: LicenseType.DriverReportModuleReadonly,
    },
    [LicenseType.PackagingModule]: {
        translation: 'packaging',
        readonlyLicense: LicenseType.PackagingModuleReadonly,
    },
    [LicenseType.DeliveryManagementModule]: {
        translation: 'deliveryManagement',
        readonlyLicense: LicenseType.DeliveryManagementModuleReadonly,
    },
    [LicenseType.PODModule]: {
        translation: 'podReceipts',
        readonlyLicense: LicenseType.PODModuleReadonly,
    },
    [LicenseType.DashboardModule]: {
        translation: 'dashboard',
    },
    [LicenseType.CargoLoadingModule]: {
        translation: 'cargoLoading',
    },
}

export const defaultModuleRoutes: DefaultModuleRoute[] = [
    {
        route: { name: 'dashboard' },
        licenses: [LicenseType.DashboardModule],
    },
    {
        route: { name: 'packaging.balance', params: { tab: 'customers' } },
        licenses: [LicenseType.PackagingModule, LicenseType.PackagingModuleReadonly],
    },
    {
        route: { name: 'pod.receipts' },
        licenses: [LicenseType.PODModule, LicenseType.PODModuleReadonly],
    },
    {
        route: { name: 'dr.reports' },
        licenses: [LicenseType.DriverReportModule, LicenseType.DriverReportModuleReadonly],
    },
    {
        route: { name: 'dmr.reports' },
        licenses: [LicenseType.DamageReportModule, LicenseType.DamageReportModuleReadonly],
    },
    {
        route: { name: 'dm.hubs' },
        licenses: [
            LicenseType.DeliveryManagementModule,
            LicenseType.DeliveryManagementModuleReadonly,
        ],
    },
    {
        route: { name: 'cl' },
        licenses: [LicenseType.CargoLoadingModule],
    },
]

export function firstModuleRoute(licenses: LicenseType[]): RouteLocationRaw {
    for (const route of defaultModuleRoutes) {
        const matchedLicenses = route.licenses.filter((license) => licenses.includes(license))
        if (matchedLicenses.length > 0) {
            return route.route
        }
    }

    return '/404'
}
